"use client"

import * as React from "react"
import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { IconChevronRight, type Icon } from "@tabler/icons-react"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"
import { useSidebarNavStore } from "@/stores/sidebar-nav-store"
import type { FeatureFlags } from "@/types"
import type { ReportSlug } from "@/lib/reports.generated"

export type NavSubItem = {
  title: string
  titleKey?: string
  url: string
  icon?: Icon
  badge?: number
  onClick?: () => void
  isActive?: boolean
  // Per-sub-item visibility gates, evaluated by app-sidebar's filterSubItems
  // (same semantics as top-level items). A parent whose children all filter
  // out is dropped entirely — never flattened to a bare link.
  permission?: string
  anyPermission?: string[]
  featureFlag?: keyof FeatureFlags
  hideWhenFlag?: keyof FeatureFlags
  /**
   * Hide when the flag is explicitly OFF — the mirror of `hideWhenFlag`, for
   * opt-OUT flags that default TRUE. It tests `=== false`, not falsiness, so a
   * payload that omits the key (older backend) keeps the item VISIBLE, which is
   * the honest reading of "no clinic has opted out". Using `featureFlag` for
   * that polarity would hide the item fleet-wide on any version skew.
   */
  hideWhenFlagOff?: keyof FeatureFlags
  role?: string
  /**
   * A report from the generated registry (config/reports.php). The one gate a
   * report entry needs: app-sidebar resolves its flags, hide-modes and domain
   * permission through lib/reports.ts, the same table the page guard and the
   * API route read.
   */
  report?: ReportSlug
}

export type NavItemGates = Pick<
  NavSubItem,
  | "permission"
  | "anyPermission"
  | "featureFlag"
  | "hideWhenFlag"
  | "hideWhenFlagOff"
  | "role"
  | "report"
>

type NavItem = {
  title: string
  titleKey?: string
  url: string
  icon?: Icon
  /** kept for back-compat with existing call sites; ignored by the
   *  sidebar-redesign visual treatment. */
  iconColor?: string
  badge?: number
  items?: NavSubItem[]
}

export type NavGroup = {
  label?: string
  labelKey?: string
  /** Stable key for persisted collapse state. Groups without an id (or
   *  without a label) render non-collapsible. */
  id?: string
  /** Initial open state for collapsible groups; defaults to open. */
  defaultOpen?: boolean
  items: NavItem[]
}

/* ───────────────────────────────────────────────────────────────────
 * Sidebar redesign (DenteX spec, 2026-07-26) — shared row treatments.
 *
 * ROW: flat items with an icon (Dashboard / Pinned). Active = white card
 * lifted off the rail (surface-3 in dark, where card == sidebar bg) with
 * the icon flipped to the accent.
 * SECTION_ROW: items inside a workspace section — text-only against the
 * section rail; the icon only materializes on the collapsed icon rail
 * (text is hidden there, so the icon is the row).
 * ─────────────────────────────────────────────────────────────────── */
const ROW =
  "gap-2.5 rounded-lg px-2.5 text-[13px] text-sidebar-foreground/75 " +
  "hover:bg-surface-3 hover:text-sidebar-foreground active:bg-surface-3 active:text-sidebar-foreground " +
  "data-[active=true]:bg-card data-[active=true]:font-medium data-[active=true]:text-sidebar-foreground data-[active=true]:shadow-xs " +
  "dark:data-[active=true]:bg-surface-3 dark:data-[active=true]:shadow-none " +
  "[&>svg]:text-text-dim data-[active=true]:[&>svg]:text-sidebar-primary"

const SECTION_ROW = cn(ROW, "[&>svg]:hidden group-data-[collapsible=icon]:[&>svg]:block")

function RowBadge({ badge, className }: { badge?: number; className?: string }) {
  if (badge === undefined || badge <= 0) return null
  return (
    <SidebarMenuBadge
      className={cn(
        "bg-primary text-primary-foreground rounded-full text-[11px] font-semibold tabular-nums",
        className
      )}
    >
      {badge > 99 ? "99+" : badge}
    </SidebarMenuBadge>
  )
}

/** Sidebar-restructure — resolve a collapsible group's persisted open state.
 *  Before zustand rehydrates we render pure defaults so the server markup
 *  matches the first client paint (no hydration mismatch); after hydration
 *  the persisted value (when present) wins. */
function resolveGroupOpen(
  group: NavGroup,
  openGroups: Record<string, boolean>,
  hasHydrated: boolean
): boolean {
  const fallback = group.defaultOpen ?? true
  if (!hasHydrated || !group.id) return fallback
  return openGroups[group.id] ?? fallback
}

/* ───────────────────────────────────────────────────────────────────
 * NavGroupSection — per-group chrome. Groups without an id or label
 * render a plain SidebarGroup with the small-caps label (Pinned). Groups
 * with both render as a workspace section: a menu-row-sized header
 * (chevron · label · item count) over a rail-lined body. Sections are an
 * EXCLUSIVE accordion (opening one closes the rest — the pinned block
 * covers quick access, so only one taxonomy branch needs to be open),
 * with open state persisted per group.id in the sidebar-nav store.
 * ─────────────────────────────────────────────────────────────────── */
function NavGroupSection({
  group,
  sectionIds,
  children,
}: {
  group: NavGroup
  /** ids of ALL collapsible sections — needed to close siblings on open. */
  sectionIds: string[]
  children: React.ReactNode
}) {
  const { state } = useSidebar()
  const openGroups = useSidebarNavStore((s) => s.openGroups)
  const hasHydrated = useSidebarNavStore((s) => s._hasHydrated)
  const setGroupOpen = useSidebarNavStore((s) => s.setGroupOpen)

  // No stable id / no label ⇒ non-collapsible (Dashboard, Pinned).
  if (!group.id || !group.label) {
    return (
      <SidebarGroup className="py-1">
        {group.label && (
          <SidebarGroupLabel className="text-text-dim h-auto px-2.5 pt-1.5 pb-1 text-[10px] font-semibold tracking-[0.14em] uppercase">
            {group.label}
          </SidebarGroupLabel>
        )}
        <SidebarGroupContent>
          <SidebarMenu className="gap-px">{children}</SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    )
  }

  const groupId = group.id
  const persistedOpen = resolveGroupOpen(group, openGroups, hasHydrated)
  // Icon-collapsed rail hides the section headers, so every item icon
  // must stay visible: force open and never write the forced state back.
  const iconMode = state === "collapsed"
  const effectiveOpen = iconMode ? true : persistedOpen
  const handleOpenChange = (open: boolean) => {
    if (iconMode) return
    if (open) {
      for (const id of sectionIds) if (id !== groupId) setGroupOpen(id, false)
    }
    setGroupOpen(groupId, open)
  }

  return (
    <Collapsible
      open={effectiveOpen}
      onOpenChange={handleOpenChange}
      className="group/navgroup"
    >
      <SidebarGroup className="py-px">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="text-sidebar-foreground hover:bg-surface-3 flex w-full items-center gap-2 rounded-lg px-2.5 py-[7px] text-left text-[13px] font-medium transition-colors group-data-[collapsible=icon]:hidden"
          >
            <IconChevronRight className="text-text-dim size-3.5 shrink-0 transition-transform duration-200 group-data-[state=open]/navgroup:rotate-90" />
            <span className="truncate">{group.label}</span>
            <span className="text-text-dim ml-auto text-[11px] tabular-nums">
              {React.Children.count(children)}
            </span>
          </button>
        </CollapsibleTrigger>
        {/* No animation classes on CollapsibleContent — avoids a visible
            post-hydration open/close swap animation. */}
        <CollapsibleContent>
          <SidebarGroupContent className="relative pt-0.5 pb-1 pl-[21px] group-data-[collapsible=icon]:p-0 before:absolute before:top-1 before:bottom-2 before:left-[13px] before:w-px before:bg-sidebar-border group-data-[collapsible=icon]:before:hidden">
            <SidebarMenu className="gap-px">{children}</SidebarMenu>
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  )
}

/* ───────────────────────────────────────────────────────────────────
 * NavAccordionItem — item with a sub-item accordion. Controlled open
 * state (was uncontrolled defaultOpen) so navigating into a child of a
 * mounted-closed accordion expands it. Auto-opens on active, never
 * auto-closes, and is intentionally NOT persisted.
 * ─────────────────────────────────────────────────────────────────── */
function NavAccordionItem({
  item,
  isActive,
  isSubItemActive,
  rowClassName = SECTION_ROW,
}: {
  item: NavItem
  isActive: (url: string) => boolean
  isSubItemActive: (subItem: NavSubItem) => boolean
  /** ROW for flat groups (Pinned — icon stays visible), SECTION_ROW inside
   *  workspace sections (text-only until the icon rail). */
  rowClassName?: string
}) {
  const router = useRouter()

  const subItems = item.items ?? []
  const hasActiveSubItem = subItems.some(isSubItemActive)
  const parentIsActive = isActive(item.url) && !hasActiveSubItem
  const anyActive = parentIsActive || hasActiveSubItem

  const [open, setOpen] = React.useState(anyActive)
  React.useEffect(() => {
    if (anyActive) setOpen(true)
  }, [anyActive])

  return (
    <Collapsible
      asChild
      open={open}
      onOpenChange={setOpen}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            isActive={parentIsActive}
            className={rowClassName}
          >
            {item.icon && <item.icon />}
            <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
            <IconChevronRight className="ml-auto !size-3.5 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <RowBadge badge={item.badge} />
        <CollapsibleContent>
          <SidebarMenuSub>
            {subItems.map((subItem) => (
              <SidebarMenuSubItem key={subItem.title} className="relative">
                <SidebarMenuSubButton
                  asChild={!subItem.onClick}
                  isActive={isSubItemActive(subItem)}
                  onClick={subItem.onClick ? () => {
                    subItem.onClick?.()
                    router.push(subItem.url)
                  } : undefined}
                >
                  {subItem.onClick ? (
                    <>
                      {subItem.icon && <subItem.icon className="h-4 w-4" />}
                      <span>{subItem.title}</span>
                    </>
                  ) : (
                    <Link href={subItem.url}>
                      {subItem.icon && <subItem.icon className="h-4 w-4" />}
                      <span>{subItem.title}</span>
                    </Link>
                  )}
                </SidebarMenuSubButton>
                <RowBadge
                  badge={subItem.badge}
                  className="!top-1 !h-4 !min-w-4 text-[9px]"
                />
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

export function NavMain({
  groups,
  workspaceLabel,
}: {
  groups: NavGroup[]
  /** Small-caps label rendered once, above the first collapsible section. */
  workspaceLabel?: string
}) {
  const pathname = usePathname()

  // Pre-compute the flat list of all nav URLs once per render.
  // Used by isActive() to detect more-specific sibling matches.
  const allUrls = React.useMemo(() => {
    const urls: string[] = []
    for (const group of groups) {
      for (const item of group.items) {
        urls.push(item.url)
        if (item.items) {
          for (const sub of item.items) {
            urls.push(sub.url)
          }
        }
      }
    }
    return urls
  }, [groups])

  // Phase 50 Bug #1 — longest-prefix-match active-state predicate.
  // An item is active iff:
  //   (a) the pathname exactly matches the item's URL, OR
  //   (b) the pathname starts with item.url + "/" AND no other nav URL that is
  //       a more-specific descendant of item.url also matches the pathname.
  // Closes both the /leads vs /leads/italian dual-highlight (CONTEXT D-01)
  // AND the latent /inventory vs /inventory/movements dual-highlight
  // (RESEARCH Pitfall 3).
  const isActive = React.useCallback(
    (url: string): boolean => {
      if (pathname === url) return true
      if (!pathname.startsWith(url + "/")) return false
      return !allUrls.some(
        (other) =>
          other !== url
          && other.startsWith(url + "/")
          && (pathname === other || pathname.startsWith(other + "/"))
      )
    },
    [pathname, allUrls]
  )

  const isSubItemActive = React.useCallback(
    (subItem: NavSubItem): boolean => {
      const routeActive = isActive(subItem.url)
      // Explicit sub-item state narrows duplicate URLs, but should not keep
      // that sub-item highlighted after navigating away from its route.
      return subItem.isActive === undefined
        ? routeActive
        : routeActive && subItem.isActive
    },
    [isActive]
  )

  const sectionIds = React.useMemo(
    () => groups.filter((g) => g.id && g.label).map((g) => g.id as string),
    [groups]
  )

  // Auto-expand a collapsed section when navigation lands on one of its
  // routes — exclusively (siblings close), preserving the one-open-branch
  // invariant. One-shot per navigation (deps: pathname only) so the user can
  // re-collapse the group and stay on the page without the effect fighting
  // back. Store values are read via getState() to avoid re-running when the
  // store itself changes.
  const setGroupOpen = useSidebarNavStore((s) => s.setGroupOpen)
  React.useEffect(() => {
    const { openGroups, _hasHydrated } = useSidebarNavStore.getState()
    for (const group of groups) {
      if (!group.id || !group.label) continue
      if (resolveGroupOpen(group, openGroups, _hasHydrated)) continue
      const containsActive = group.items.some(
        (item) =>
          isActive(item.url) || (item.items?.some(isSubItemActive) ?? false)
      )
      if (containsActive) {
        for (const id of sectionIds) if (id !== group.id) setGroupOpen(id, false)
        setGroupOpen(group.id, true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pathname is the
    // only intended trigger; groups/helpers/store are read fresh each run.
  }, [pathname])

  const firstSectionIdx = groups.findIndex((g) => g.id && g.label)

  return (
    <>
      {groups.map((group, groupIdx) => {
        const isSection = Boolean(group.id && group.label)
        return (
          <React.Fragment key={group.id ?? group.label ?? `nav-group-${groupIdx}`}>
            {workspaceLabel && groupIdx === firstSectionIdx && (
              <div className="text-text-dim px-4.5 pt-2.5 pb-1 text-[10px] font-semibold tracking-[0.14em] uppercase group-data-[collapsible=icon]:hidden">
                {workspaceLabel}
              </div>
            )}
            <NavGroupSection group={group} sectionIds={sectionIds}>
              {group.items.map((item) => {
                if (item.items) {
                  return (
                    <NavAccordionItem
                      key={item.title}
                      item={item}
                      isActive={isActive}
                      isSubItemActive={isSubItemActive}
                      rowClassName={isSection ? SECTION_ROW : ROW}
                    />
                  )
                }

                const active = isActive(item.url)
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      tooltip={item.title}
                      isActive={active}
                      className={isSection ? SECTION_ROW : ROW}
                    >
                      <Link href={item.url}>
                        {item.icon && <item.icon />}
                        <span className="group-data-[collapsible=icon]:hidden">
                          {item.title}
                        </span>
                      </Link>
                    </SidebarMenuButton>
                    <RowBadge badge={item.badge} />
                  </SidebarMenuItem>
                )
              })}
            </NavGroupSection>
          </React.Fragment>
        )
      })}
    </>
  )
}
